import { menuItemDetailQueryOptions } from '@/api/queries/menu-items';
import { ScreenHeader } from '@/components/layout/screen-header';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useAppTheme } from '@/providers/theme-provider';
import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAdminApproveMenuItem, useAdminUpdateMenuItem } from '../hooks/use-admin-menu-items';

export function AdminEditMenuItemScreen() {
  const theme = useAppTheme();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const { data: item, isLoading } = useQuery(menuItemDetailQueryOptions(id!));

  const updateMutation = useAdminUpdateMenuItem();
  const approveMutation = useAdminApproveMenuItem();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (item) {
      setName(item.name);
      setDescription(item.description ?? '');
    }
  }, [item]);

  const handleSave = () => {
    if (!id || !name.trim()) return;

    updateMutation.mutate(
      { id, data: { name: name.trim(), description: description.trim() || undefined } },
      {
        onSuccess: () => {
          toast.success('Menu item updated');
          router.back();
        },
        onError: (err) => {
          toast.error(err.message);
        },
      }
    );
  };

  const handleSaveAndApprove = () => {
    if (!id || !name.trim()) return;

    updateMutation.mutate(
      { id, data: { name: name.trim(), description: description.trim() || undefined } },
      {
        onSuccess: () => {
          approveMutation.mutate(id, {
            onSuccess: () => {
              toast.success('Menu item approved');
              router.back();
            },
            onError: (err) => {
              toast.error('Saved but failed to approve', { description: err.message });
            },
          });
        },
        onError: (err) => {
          toast.error(err.message);
        },
      }
    );
  };

  const isSaving = updateMutation.isPending || approveMutation.isPending;

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 bg-background">
        <ScreenHeader title="Edit Menu Item" showBackButton />
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (!item) {
    return (
      <SafeAreaView className="flex-1 bg-background">
        <ScreenHeader title="Edit Menu Item" showBackButton />
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text className="text-muted-foreground">Menu item not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-background">
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <ScreenHeader title="Edit Menu Item" showBackButton />

        {item.businessName && (
          <Text className="text-muted-foreground" style={{ fontSize: 13, marginBottom: 16, paddingHorizontal: 4 }}>
            {item.businessName}
          </Text>
        )}

        <TextInput label="Name" containerClassName="mb-4" value={name} onChangeText={setName} placeholder="Menu item name" />

        <TextInput label="Description" containerClassName="mb-6" value={description} onChangeText={setDescription} placeholder="Description (optional)" multiline numberOfLines={4} />

        <Pressable
          onPress={handleSave}
          disabled={isSaving || !name.trim()}
          className="bg-surface-secondary"
          style={{
            borderRadius: 10,
            paddingVertical: 14,
            alignItems: 'center',
            marginBottom: 10,
            opacity: isSaving || !name.trim() ? 0.5 : 1,
          }}
        >
          <Text className="text-foreground" style={{ fontSize: 16, fontWeight: '600' }}>
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Text>
        </Pressable>

        {item.status === 'pending' && (
          <Pressable
            onPress={handleSaveAndApprove}
            disabled={isSaving || !name.trim()}
            className="bg-primary"
            style={{
              borderRadius: 10,
              paddingVertical: 14,
              alignItems: 'center',
              opacity: isSaving || !name.trim() ? 0.5 : 1,
            }}
          >
            <Text className="text-background" style={{ fontSize: 16, fontWeight: '600' }}>
              {approveMutation.isPending ? 'Approving...' : 'Save & Approve'}
            </Text>
          </Pressable>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
